/**
 * PhysicalAttributesInput Component
 * 
 * Combined height and weight input for athlete physical attributes
 * Ensures consistent alignment and styling
 * Created: 2025-11-06
 */

import React from 'react';
import TextField from '@/components/form/fields/TextField';
import Dropdown, { DropdownOption } from '@/components/form/Dropdown';
import { Label } from '@/components/form';

interface PhysicalAttributesInputProps {
  heightFeet?: number;
  heightInches?: number;
  weight?: number;
  onHeightChange: (feet: number | undefined, inches: number | undefined) => void;
  onWeightChange: (weight: number | undefined) => void;
  heightError?: string;
  weightError?: string;
  readOnly?: boolean;
}

const FEET_OPTIONS: DropdownOption[] = [
  { value: '', label: 'Select...' },
  { value: '3', label: "3'" },
  { value: '4', label: "4'" },
  { value: '5', label: "5'" },
  { value: '6', label: "6'" },
  { value: '7', label: "7'" },
];

const INCHES_OPTIONS: DropdownOption[] = [
  { value: '', label: 'Select...' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i.toString(),
    label: `${i}"`
  }))
];

const PhysicalAttributesInput: React.FC<PhysicalAttributesInputProps> = ({
  heightFeet,
  heightInches,
  weight,
  onHeightChange,
  onWeightChange,
  heightError,
  weightError,
  readOnly = false
}) => {
  const handleFeetChange = (value: string) => {
    const feet = value ? parseFloat(value) : undefined;
    onHeightChange(feet, heightInches);
  };

  const handleInchesChange = (value: string) => {
    const inches = value ? parseFloat(value) : undefined;
    onHeightChange(heightFeet, inches);
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? parseFloat(e.target.value) : undefined;
    onWeightChange(value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Height Input */}
      <div>
        <Label id="height-label" htmlFor="heightFeet">Height (Optional)</Label>
        <div className="flex gap-2">
          <div className="flex-1">
            <span id="heightFeet-unit" className="sr-only">Feet</span>
            <Dropdown
              id="heightFeet"
              ariaLabelledBy="height-label heightFeet-unit"
              name="heightFeet"
              value={heightFeet?.toString() || ''}
              onChange={handleFeetChange}
              options={FEET_OPTIONS}
              disabled={readOnly}
              placeholder="Feet"
            />
          </div>
          
          <div className="flex-1">
            <span id="heightInches-unit" className="sr-only">Inches</span>
            <Dropdown
              id="heightInches"
              ariaLabelledBy="height-label heightInches-unit"
              name="heightInches"
              value={heightInches?.toString() || ''}
              onChange={handleInchesChange}
              options={INCHES_OPTIONS}
              disabled={readOnly}
              placeholder="Inches"
            />
          </div>
        </div>
        
        {heightError && (
          <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{heightError}</p>
        )}
        
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          💡 Height helps us recommend the best positions for stunts
        </p>
      </div>

      {/* Weight Input */}
      <div>
        <TextField
          id="weight"
          name="weight"
          label="Weight (Optional)"
          type="number"
          value={weight?.toString() || ''}
          onChange={handleWeightChange}
          error={weightError}
          placeholder="e.g., 125"
          min={50}
          max={300}
          step={0.5}
          disabled={readOnly}
          helpText="💡 Weight helps with flyer/base matching"
        />
      </div>
    </div>
  );
};

export default PhysicalAttributesInput;

